import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { createPortal } from 'react-dom';
import {
  FiPlus,
  FiX,
  FiSearch,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiFilter,
  FiSettings,
  FiRefreshCw
} from 'react-icons/fi';
import './assets/css/ProtonCalendar.scss';
import './assets/css/DayEventsModal.scss';
import './assets/css/EventPopover.scss';
import { protonCalendarAPI } from '../../services/calendarAPI';
import CreateEventModal from './components/CreateEventModal';

// Month and day names
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ProtonCalendar = ({ 
  isFullscreen = true, 
  onToggleFullscreen = null, 
  onClose = null,
  classId = null   // Optional: filter by specific class
}) => {
  const auth = useSelector(state => state.auth);
  const instituteId = auth?.institute?.institute_id ?? auth?.user?.institute_id;
  const academicId  = auth?.current_academic_id;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isCompact, setIsCompact] = useState(window.innerWidth <= 480);
  // const [viewMode, setViewMode] = useState('month'); // TODO: Implement different view modes
  const [showEventFilter, setShowEventFilter] = useState(false);
  const [eventTypeFilter, setEventTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [dynamicCellHeight, setDynamicCellHeight] = useState(110);
  const [showDayEvents, setShowDayEvents] = useState(false);
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [selectedDayDate, setSelectedDayDate] = useState(null);
  // Event popover modal state
  const [showEventPopover, setShowEventPopover] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });
  const [arrowPosition, setArrowPosition] = useState('top');
  // View More popover modal state
  const [showViewMorePopover, setShowViewMorePopover] = useState(false);
  const [viewMoreEvents, setViewMoreEvents] = useState([]);
  const [viewMorePosition, setViewMorePosition] = useState({ x: 0, y: 0 });
  const [viewMoreArrowPosition, setViewMoreArrowPosition] = useState('top');
  const [viewMoreDate, setViewMoreDate] = useState(null);
  const [loadingCellDate, setLoadingCellDate] = useState(null);
  const [apiError, setApiError] = useState(false);
  
  // Event creation modal state
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [createEventPosition, setCreateEventPosition] = useState(null);
  const [createEventDate, setCreateEventDate] = useState(null);
  const [createEventArrowDirection, setCreateEventArrowDirection] = useState('top');
  
  // Event editing state
  const [editingEvent, setEditingEvent] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Load events from API based on current month and filters
  const loadCalendarEvents = useCallback(async (startDate, endDate) => {
    try {
      setLoading(true);
      setError(null);
      setApiError(false);

      // Format dates properly to avoid timezone issues
      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const params = {
        start_date: formatDate(startDate),
        end_date: formatDate(endDate),
        institute_id: instituteId,
        academic_id: academicId,
        class_id: classId,
        event_types: eventTypeFilter !== 'all' ? [eventTypeFilter] : undefined,
        search: searchQuery && searchQuery !== 'search' ? searchQuery : undefined,
        limit: 500
      };

      const response = await protonCalendarAPI.getEvents(params);
      
      if (response.success) {
        setEvents(response.data.events || []);
      } else {
        setError(response.message || 'Failed to load events');
        setApiError(true);
        setEvents([]); // Fallback to empty array
      }
    } catch (err) {
      setError('Failed to connect to server');
      setApiError(true);
      setEvents([]); // Fallback to empty array
      console.error('Calendar API Error:', err);
    } finally {
      setLoading(false);
    }
  }, [eventTypeFilter, searchQuery, instituteId, academicId, classId]);

  // Check if any filters are applied
  const hasActiveFilters = () => {
    return eventTypeFilter !== 'all' || (searchQuery && searchQuery.trim() && searchQuery !== 'search');
  };

  // Clear all filters
  const clearAllFilters = () => {
    setEventTypeFilter('all');
    setSearchQuery('');
    setShowSearchInput(false);
  };

  // Helper function to get visible calendar date range
  const getVisibleDateRange = useCallback(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    
    // This should match exactly what getCalendarDays() generates
    let dates = [];
    
    // Previous month days (only if needed to fill first week)
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    for (let i = firstDay - 1; i >= 0; i--) {
      const prevDate = daysInPrevMonth - i;
      const prevFullDate = new Date(year, month - 1, prevDate);
      dates.push(prevFullDate);
    }

    // Current month days
    for (let date = 1; date <= daysInMonth; date++) {
      const fullDate = new Date(year, month, date);
      dates.push(fullDate);
    }

    // Next month days (only to complete the last week)
    const totalCells = dates.length;
    const remainingCellsInLastWeek = totalCells % 7;
    if (remainingCellsInLastWeek !== 0) {
      const nextMonthDaysNeeded = 7 - remainingCellsInLastWeek;
      for (let date = 1; date <= nextMonthDaysNeeded; date++) {
        const nextFullDate = new Date(year, month + 1, date);
        dates.push(nextFullDate);
      }
    }
    
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    
    return { startDate, endDate };
  }, [currentDate]);

  // Load events when month changes or filters change
  useEffect(() => {
    const { startDate, endDate } = getVisibleDateRange();
    loadCalendarEvents(startDate, endDate);
  }, [currentDate, loadCalendarEvents, getVisibleDateRange]);

  // Debounced search effect
  useEffect(() => {
    if (searchQuery && searchQuery !== 'search') {
      const debounceTimer = setTimeout(() => {
        const { startDate, endDate } = getVisibleDateRange();
        loadCalendarEvents(startDate, endDate);
      }, 500); // 500ms delay

      return () => clearTimeout(debounceTimer);
    }
  }, [searchQuery, getVisibleDateRange, loadCalendarEvents]);

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  // Helper function for height calculation (without state dependencies)
  const getCalendarDaysForHeight = useCallback(() => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    
    const days = [];

    // Previous month days (only if needed to fill first week)
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ temp: true });
    }

    // Current month days
    for (let date = 1; date <= daysInMonth; date++) {
      days.push({ temp: true });
    }

    // Next month days (only to complete the last week)
    const totalCells = days.length;
    const remainingCellsInLastWeek = totalCells % 7;
    if (remainingCellsInLastWeek !== 0) {
      const nextMonthDaysNeeded = 7 - remainingCellsInLastWeek;
      for (let date = 1; date <= nextMonthDaysNeeded; date++) {
        days.push({ temp: true });
      }
    }

    return days;
  }, [currentDate]);

  // Enhanced dynamic cell height calculation with perfect space utilization
  useEffect(() => {
    const calculateOptimalCellHeight = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsCompact(window.innerWidth <= 480);
      
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        try {
          // Get calendar container and key measurements
          const calendarContainer = document.querySelector('.proton-calendar');
          const calendarBody = document.querySelector('.calendar-body');
          const monthView = document.querySelector('.month-view');
          
          if (!calendarContainer || !calendarBody || !monthView) return;
          
          // Get actual heights of UI elements
          const header = document.querySelector('.calendar-header');
          const eventFilter = document.querySelector('.filter-dropdown');
          
          const headerHeight = header ? header.offsetHeight : 0;
          const filterHeight = (eventFilter && showEventFilter) ? eventFilter.offsetHeight : 0;
          
          // Calculate total available height for calendar body
          const viewportHeight = window.innerHeight;
          const nonCalendarHeight = headerHeight + filterHeight;
          const calendarBodyHeight = viewportHeight - nonCalendarHeight;
          
          // Account for calendar body padding and grid gap
          const bodyPadding = 16; // Calendar body internal padding
          const gridGap = 1; // Gap between cells
          const safetyMargin = 8; // Prevent overflow
          
          const availableGridHeight = calendarBodyHeight - bodyPadding - safetyMargin;
          
          // Calculate number of weeks needed for current month
          const calendarDays = getCalendarDaysForHeight();
          const totalCells = calendarDays.length;
          const numberOfRows = Math.ceil(totalCells / 7);
          
          // Calculate optimal cell height to fill available space perfectly
          const totalGapHeight = (numberOfRows - 1) * gridGap;
          const cellHeight = Math.floor((availableGridHeight - totalGapHeight) / numberOfRows);
          
          // Set device-specific constraints
          const constraints = {
            compact: { min: 70, max: 110 },   // Mobile phones
            mobile: { min: 80, max: 130 },    // Tablets
            desktop: { min: 90, max: 180 }    // Desktop
          };
          
          const currentConstraints = isCompact ? constraints.compact : 
                                   isMobile ? constraints.mobile : 
                                   constraints.desktop;
          
          // Apply constraints while maintaining optimal fit
          let finalCellHeight = Math.max(
            currentConstraints.min, 
            Math.min(cellHeight, currentConstraints.max)
          );
          
          // If constrained height doesn't fit perfectly, calculate best compromise
          const totalConstrainedHeight = (finalCellHeight * numberOfRows) + totalGapHeight;
          if (totalConstrainedHeight > availableGridHeight) {
            // Reduce height proportionally
            finalCellHeight = Math.max(
              currentConstraints.min, 
              Math.floor((availableGridHeight - totalGapHeight) / numberOfRows)
            );
          }
          
          // Set the height
          setDynamicCellHeight(finalCellHeight);
          
          // Set CSS custom properties for perfect grid layout
          const gridElement = document.querySelector('.calendar-grid');
          if (gridElement) {
            gridElement.style.setProperty('--cell-height', `${finalCellHeight}px`);
            gridElement.style.setProperty('--cell-min-height', `${currentConstraints.min}px`);
            gridElement.style.setProperty('--number-of-rows', numberOfRows.toString());
          }
          
          // Development debugging
          if (process.env.NODE_ENV === 'development') {
            console.log('Enhanced Calendar Height Calculation:', {
              viewport: { height: viewportHeight, width: window.innerWidth },
              layout: {
                headerHeight,
                filterHeight,
                calendarBodyHeight,
                availableGridHeight,
                bodyPadding,
                safetyMargin
              },
              grid: {
                totalCells,
                numberOfRows,
                gridGap: totalGapHeight,
                calculatedCellHeight: cellHeight,
                finalCellHeight,
                totalGridHeight: totalConstrainedHeight,
                utilization: `${((totalConstrainedHeight / availableGridHeight) * 100).toFixed(1)}%`
              },
              device: {
                type: isCompact ? 'compact' : isMobile ? 'mobile' : 'desktop',
                constraints: currentConstraints
              },
              month: `${MONTH_NAMES[currentDate.getMonth()]} ${currentDate.getFullYear()}`,
              perfectFit: totalConstrainedHeight <= availableGridHeight
            });
          }
          
        } catch (error) {
          console.warn('Error in height calculation:', error);
          // Fallback heights
          const fallback = isCompact ? 75 : isMobile ? 90 : 110;
          setDynamicCellHeight(fallback);
          
          // Set fallback CSS properties
          const gridElement = document.querySelector('.calendar-grid');
          if (gridElement) {
            gridElement.style.setProperty('--cell-height', `${fallback}px`);
            gridElement.style.setProperty('--cell-min-height', `${fallback - 10}px`);
          }
        }
      }, 100);
    };

    calculateOptimalCellHeight();
    
    // Debounced resize handler
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateOptimalCellHeight, 200);
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [showEventFilter, currentDate, isMobile, isCompact, getCalendarDaysForHeight]);

  // Optimized month navigation for mobile
  const navigateMonth = useCallback((direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  }, []);

  // Handle close button click - go back to previous page
  const handleClose = () => {
    if (onToggleFullscreen) {
      onToggleFullscreen();
    } 
    if(onClose) {
      onClose(false);
    }
  };

  // Handle "View More" events click with popover display
  const handleViewMoreEvents = async (date, allEvents, e) => {
    try {
      // Get the clicked element and its parent cell
      const viewMoreElement = e.currentTarget;
      const calendarCell = viewMoreElement.closest('.calendar-cell');
      
      if (!calendarCell) return;
      
      // Calculate popover position
      const cellRect = calendarCell.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      // Popover dimensions (estimated for view more)
      const popoverWidth = 350;
      const popoverHeight = Math.min(300, allEvents.length * 60 + 80); // Dynamic height based on events
      const arrowSize = 8;
      
      // Calculate optimal position
      let x = cellRect.left + (cellRect.width / 2) - (popoverWidth / 2);
      let y = cellRect.bottom + arrowSize;
      let arrow = 'top';
      
      // Adjust horizontal position if it goes off screen
      if (x < 10) {
        x = 10;
      } else if (x + popoverWidth > viewportWidth - 10) {
        x = viewportWidth - popoverWidth - 10;
      }
      
      // Check if popover fits below the cell
      if (y + popoverHeight > viewportHeight - 10) {
        // Show above the cell instead
        y = cellRect.top - popoverHeight - arrowSize;
        arrow = 'bottom';
        
        // If it still doesn't fit above, show to the side
        if (y < 10) {
          y = cellRect.top + (cellRect.height / 2) - (popoverHeight / 2);
          
          // Determine which side has more space
          const leftSpace = cellRect.left;
          const rightSpace = viewportWidth - cellRect.right;
          
          if (rightSpace > leftSpace && rightSpace > popoverWidth + 20) {
            x = cellRect.right + arrowSize;
            arrow = 'left';
          } else if (leftSpace > popoverWidth + 20) {
            x = cellRect.left - popoverWidth - arrowSize;
            arrow = 'right';
          } else {
            // Fallback to bottom with adjusted y
            y = Math.max(10, Math.min(cellRect.bottom + arrowSize, viewportHeight - popoverHeight - 10));
            arrow = 'top';
          }
        }
      }

      // Format date properly to avoid timezone issues
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      // Set local loading state for this specific cell
      setLoadingCellDate(dateStr);
      
      const params = {
        institute_id: instituteId,
        academic_id: academicId,
        class_id: classId,
        event_types: eventTypeFilter !== 'all' ? [eventTypeFilter] : undefined
      };

      const response = await protonCalendarAPI.getEventsByDate(dateStr, params);
      
      let eventsToShow = allEvents;
      if (response.success && response.data.events) {
        eventsToShow = response.data.events;
      }
      
      setViewMoreDate(date);
      setViewMoreEvents(eventsToShow);
      setViewMorePosition({ x, y });
      setViewMoreArrowPosition(arrow);
      setShowViewMorePopover(true);
      
    } catch (err) {
      console.error('Failed to fetch detailed day events:', err);
      // Show local events in popover
      const cellRect = e.currentTarget.closest('.calendar-cell').getBoundingClientRect();
      const x = cellRect.left + (cellRect.width / 2) - 175; // 350/2
      const y = cellRect.bottom + 8;
      
      setViewMoreDate(date);
      setViewMoreEvents(allEvents);
      setViewMorePosition({ x, y });
      setViewMoreArrowPosition('top');
      setShowViewMorePopover(true);
    } finally {
      setLoadingCellDate(null);
    }
  };

  // Close day events modal
  const closeDayEventsModal = () => {
    setShowDayEvents(false);
    setSelectedDayEvents([]);
    setSelectedDayDate(null);
  };

  const goToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  // Calendar calculation functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    
    const days = [];
    const today = new Date();

    // Previous month days (only if needed to fill first week)
    const prevMonth = new Date(year, month - 1, 0);
    const daysInPrevMonth = prevMonth.getDate();
    
    for (let i = firstDay - 1; i >= 0; i--) {
      const prevDate = daysInPrevMonth - i;
      const prevFullDate = new Date(year, month - 1, prevDate);
      // Don't highlight today for other month cells
      const prevIsToday = false;
        
      days.push({
        date: prevDate,
        isCurrentMonth: false,
        isPreviousMonth: true,
        isToday: prevIsToday,
        fullDate: prevFullDate
      });
    }

    // Current month days
    for (let date = 1; date <= daysInMonth; date++) {
      const fullDate = new Date(year, month, date);
      const isToday = 
        fullDate.getDate() === today.getDate() &&
        fullDate.getMonth() === today.getMonth() &&
        fullDate.getFullYear() === today.getFullYear();

      days.push({
        date,
        isCurrentMonth: true,
        isPreviousMonth: false,
        isNextMonth: false,
        isToday,
        fullDate
      });
    }

    // Next month days (only to complete the last week)
    const totalCells = days.length;
    const remainingCellsInLastWeek = totalCells % 7;
    if (remainingCellsInLastWeek !== 0) {
      const nextMonthDaysNeeded = 7 - remainingCellsInLastWeek;
      for (let date = 1; date <= nextMonthDaysNeeded; date++) {
        const nextFullDate = new Date(year, month + 1, date);
        // Don't highlight today for other month cells
        const nextIsToday = false;
          
        days.push({
          date,
          isCurrentMonth: false,
          isPreviousMonth: false,
          isNextMonth: true,
          isToday: nextIsToday,
          fullDate: nextFullDate
        });
      }
    }

    return days;
  };

  // Get events for a specific date (already filtered by API)
  const getEventsForDate = (date) => {
    // Format the target date properly to avoid timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    let dayEvents = events.filter(event => {
      // Add null checks for event properties
      if (!event || (!event.date && !event.start)) {
        return false;
      }
      
      // Handle the new API format (start/end) and legacy format (date)
      let eventDate;
      if (event.start) {
        // New optimized format - use start datetime
        eventDate = event.start.includes('T') ? event.start.split('T')[0] : event.start;
      } else if (event.date) {
        // Legacy format
        eventDate = event.date.includes('T') ? event.date.split('T')[0] : event.date;
      } else {
        return false;
      }
      
      const matches = eventDate === dateStr;
      
      return matches;
    });
    
    // Sort events by time (all-day events first, then by start time).
    // Support both key variants: isAllDay (current) and allDay (legacy).
    dayEvents.sort((a, b) => {
      const aAllDay = a.isAllDay ?? a.allDay ?? false;
      const bAllDay = b.isAllDay ?? b.allDay ?? false;
      if (aAllDay && !bAllDay) return -1;
      if (!aAllDay && bAllDay) return 1;
      
      // startTime is the unified key; fall back to start for legacy events
      const aStartTime = a.startTime || a.start;
      const bStartTime = b.startTime || b.start;
      
      if (aStartTime && bStartTime) {
        return aStartTime.localeCompare(bStartTime);
      }
      return 0;
    });
    
    return dayEvents;
  };

  // Handle event click with popover modal
  const handleEventClick = async (event, e) => {
    e.stopPropagation();
    
    // Get the clicked element and its parent cell
    const eventElement = e.currentTarget;
    const calendarCell = eventElement.closest('.calendar-cell');
    
    if (!calendarCell) return;
    
    // Calculate popover position
    const cellRect = calendarCell.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    
    // Popover dimensions (estimated)
    const popoverWidth = 300;
    const popoverHeight = 200;
    const arrowSize = 8;
    
    // Calculate optimal position
    let x = cellRect.left + (cellRect.width / 2) - (popoverWidth / 2);
    let y = cellRect.bottom + arrowSize;
    let arrow = 'top';
    
    // Adjust horizontal position if it goes off screen
    if (x < 10) {
      x = 10;
    } else if (x + popoverWidth > viewportWidth - 10) {
      x = viewportWidth - popoverWidth - 10;
    }
    
    // Check if popover fits below the cell
    if (y + popoverHeight > viewportHeight - 10) {
      // Show above the cell instead
      y = cellRect.top - popoverHeight - arrowSize;
      arrow = 'bottom';
      
      // If it still doesn't fit above, show to the side
      if (y < 10) {
        y = cellRect.top + (cellRect.height / 2) - (popoverHeight / 2);
        
        // Determine which side has more space
        const leftSpace = cellRect.left;
        const rightSpace = viewportWidth - cellRect.right;
        
        if (rightSpace > leftSpace && rightSpace > popoverWidth + 20) {
          x = cellRect.right + arrowSize;
          arrow = 'left';
        } else if (leftSpace > popoverWidth + 20) {
          x = cellRect.left - popoverWidth - arrowSize;
          arrow = 'right';
        } else {
          // Fallback to bottom with adjusted y
          y = Math.max(10, Math.min(cellRect.bottom + arrowSize, viewportHeight - popoverHeight - 10));
          arrow = 'top';
        }
      }
    }
    
    // Check if we need to fetch detailed information
    let eventToShow = event;
    
    // If this is an optimized response without full details, fetch them
    if (event.assignment_summary && !event.assignment_details) {
      try {
        const detailedEvent = await protonCalendarAPI.getEventDetails(event.id);
        if (detailedEvent.success) {
          eventToShow = { ...event, ...detailedEvent.data };
        }
      } catch (error) {
        console.error('Failed to fetch event details:', error);
        // Use original event if detailed fetch fails
      }
    }
    
    setSelectedEvent(eventToShow);
    setPopoverPosition({ x, y });
    setArrowPosition(arrow);
    setShowEventPopover(true);
  };

  // Close event popover
  const closeEventPopover = () => {
    setShowEventPopover(false);
    setSelectedEvent(null);
    setPopoverPosition({ x: 0, y: 0 });
    setArrowPosition('top');
  };

  // Close view more popover
  const closeViewMorePopover = () => {
    setShowViewMorePopover(false);
    setViewMoreEvents([]);
    setViewMorePosition({ x: 0, y: 0 });
    setViewMoreArrowPosition('top');
    setViewMoreDate(null);
  };

  // Handle add event button click
  const handleAddEventClick = (e) => {
    e.preventDefault();
    
    // Calculate position for modal
    const buttonRect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: buttonRect.left + (buttonRect.width / 2),
      y: buttonRect.bottom + 8
    };
    
    setCreateEventPosition(position);
    setCreateEventDate(new Date()); // Default to today
    setCreateEventArrowDirection('top');
    setShowCreateEventModal(true);
  };

  // Handle cell click for creating events
  const handleCellDoubleClick = (date, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Calculate position relative to the clicked cell
    const cellRect = e.currentTarget.getBoundingClientRect();
    const position = {
      x: cellRect.left + (cellRect.width / 2),
      y: cellRect.top + (cellRect.height / 2)
    };
    
    setCreateEventPosition(position);
    setCreateEventDate(date);
    setCreateEventArrowDirection('top');
    setShowCreateEventModal(true);
  };

  // Handle create event modal close
  const handleCreateEventClose = (wasCreated = false) => {
    setShowCreateEventModal(false);
    setCreateEventPosition(null);
    setCreateEventDate(null);
    setCreateEventArrowDirection('top');
    
    // Clear editing state
    setEditingEvent(null);
    setIsEditMode(false);
    
    // Refresh calendar if event was created
    if (wasCreated) {
      const { startDate, endDate } = getVisibleDateRange();
      loadCalendarEvents(startDate, endDate);
    }
  };

  // Calculate maximum events that can be displayed in a cell with better vertical utilization
  const getMaxEventsForCell = useCallback(() => {
    const headerHeight = 28; // Day number header height
    const availableHeight = dynamicCellHeight - headerHeight - 8; // Subtract padding
    const eventHeight = isCompact ? 18 : isMobile ? 20 : 22; // Height per event including margin
    
    // Calculate how many events can fit vertically
    const maxEvents = Math.floor(availableHeight / eventHeight);
    
    // Set sensible limits based on screen size
    if (isCompact) {
      return Math.max(2, Math.min(maxEvents, 4));
    } else if (isMobile) {
      return Math.max(3, Math.min(maxEvents, 5));
    } else {
      return Math.max(4, Math.min(maxEvents, 7));
    }
  }, [dynamicCellHeight, isCompact, isMobile]);

  // Get visible events and check if "View More" is needed
  const getVisibleEventsAndMore = useCallback((allEvents) => {
    const maxEvents = getMaxEventsForCell();
    const shouldShowMore = allEvents.length > maxEvents;
    const visibleEvents = shouldShowMore ? allEvents.slice(0, maxEvents) : allEvents;
    const hiddenCount = shouldShowMore ? allEvents.length - maxEvents : 0;
    
    return {
      visibleEvents,
      hiddenCount,
      shouldShowMore
    };
  }, [getMaxEventsForCell]);
  const formatTime = (datetime) => {
    if (!datetime) return '';
    
    // Handle both ISO datetime strings and time-only strings
    let timeStr;
    if (datetime.includes('T')) {
      // ISO datetime - extract time part
      const date = new Date(datetime);
      timeStr = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (datetime.includes(':')) {
      // Time-only string like "14:30"
      const [hours, minutes] = datetime.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      timeStr = `${displayHour}:${minutes} ${ampm}`;
    } else {
      return datetime;
    }
    
    // Shorter format on mobile
    if (isCompact) {
      return timeStr.replace(' ', '').toLowerCase();
    }
    return timeStr;
  };

  // Handle edit event
  const handleEditEvent = (event) => {
    // Close the popover first
    closeEventPopover();
    
    // Set editing state
    setEditingEvent(event);
    setIsEditMode(true);
    
    // Set up the create event modal with existing event data for editing
    setCreateEventDate(new Date(event.start));
    setCreateEventPosition({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
    setCreateEventArrowDirection('top');
    setShowCreateEventModal(true);
  };

  // Handle delete event
  const handleDeleteEvent = async (event) => {
    // Show confirmation dialog
    const confirmed = window.confirm(
      `Are you sure you want to delete "${event.title}"? This action cannot be undone.`
    );
    
    if (!confirmed) {
      return;
    }
    
    try {
      setLoading(true);
      
      const response = await protonCalendarAPI.deleteEvent(event.id);
      
      if (response.success) {
        // Close popover
        closeEventPopover();
        
        // Refresh calendar
        const { startDate, endDate } = getVisibleDateRange();
        loadCalendarEvents(startDate, endDate);
        
        // Show success message (you can replace this with a toast notification)
        alert('Event deleted successfully');
      } else {
        alert(response.message || 'Failed to delete event');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Responsive month/year display
  const getResponsiveMonthYear = () => {
    const monthName = MONTH_NAMES[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    
    if (isCompact) {
      return `${monthName.substring(0, 3)} ${year}`;
    }
    return `${monthName} ${year}`;
  };

  const CalendarContent = () => (
    <div className={`proton-calendar ${isFullscreen ? 'fullscreen-mode' : ''}`}>
      {/* Header */}
      <div className="calendar-header">
        <div className="header-left">
          {/* Calendar Icon and Title */}
          <div className="calendar-brand">
            <FiCalendar className="calendar-icon" size={isMobile ? 20 : 24} />
            <h1 className="app-title">Proton Calendar</h1>
          </div>

          {/* View Mode Selector */}
        </div>

        <div className="header-center">
          <button className="today-btn" onClick={goToToday}>
            {isCompact ? 'Today' : 'Today'}
          </button>
          <div className="month-navigation">
            <button 
              className="nav-btn prev-btn" 
              onClick={() => navigateMonth(-1)}
              aria-label="Previous month"
              disabled={loading}
            >
              <FiChevronLeft size={isMobile ? 18 : 20} />
            </button>
            <button 
              className="nav-btn next-btn" 
              onClick={() => navigateMonth(1)}
              aria-label="Next month"
              disabled={loading}
            >
              <FiChevronRight size={isMobile ? 18 : 20} />
            </button>
          </div>
          <h2 className="calendar-title">
            {getResponsiveMonthYear()}
          </h2>
        </div>

        <div className="header-right">
          {/* Search */}
          <div className="search-container">
            <button 
              className={`icon-btn search-btn ${showSearchInput ? 'search-active' : ''}`}
              aria-label="Search events"
              onClick={(e) => {
                e.preventDefault();
                setShowSearchInput(!showSearchInput);
              }}
            >
              <FiSearch size={isMobile ? 18 : 20} />
            </button>
            <input
              type="text"
              placeholder="Search events..."
              className={`search-input ${showSearchInput ? 'search-input-visible' : ''}`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  const { startDate, endDate } = getVisibleDateRange();
                  loadCalendarEvents(startDate, endDate);
                }
              }}
              onBlur={() => {
                // Keep search input open if there's content
                if (!searchQuery) {
                  setShowSearchInput(false);
                }
              }}
              autoFocus={showSearchInput}
            />
          </div>

          {/* Event Filter */}
          <div className="filter-container">
            <button 
              className={`icon-btn filter-btn ${showEventFilter ? 'active' : ''}`}
              aria-label="Filter events"
              onClick={() => setShowEventFilter(!showEventFilter)}
              title={hasActiveFilters() ? 'Filters applied - Click to manage' : 'Filter events'}
            >
              <FiFilter size={isMobile ? 18 : 20} />
            </button>
            {hasActiveFilters() && (
              <div 
                className="filter-active-indicator" 
                title="Clear all filters"
                onClick={(e) => {
                  e.stopPropagation();
                  clearAllFilters();
                }}
              >
                <FiX size={10} />
              </div>
            )}
          </div>

          {/* Settings */}
          <button className="icon-btn settings-btn" aria-label="Settings">
            <FiSettings size={isMobile ? 18 : 20} />
          </button>

          {/* Refresh */}
          <div className="refresh-container">
            <button 
              className={`icon-btn refresh-btn ${loading ? 'loading' : ''}`}
              aria-label="Refresh events"
              onClick={() => {
                const { startDate, endDate } = getVisibleDateRange();
                loadCalendarEvents(startDate, endDate);
              }}
              disabled={loading}
              title={apiError ? error || 'API request failed' : 'Refresh events'}
            >
              <FiRefreshCw size={isMobile ? 18 : 20} />
            </button>
            {apiError && (
              <div className="api-error-indicator" title={error || 'API request failed'}>
                <span>!</span>
              </div>
            )}
          </div>

          {/* Add Event */}
          <button 
            className="icon-btn add-btn primary-btn" 
            aria-label="Add event"
            onClick={handleAddEventClick}
            title="Create new event or meeting"
          >
            <FiPlus size={isMobile ? 18 : 20} />
          </button>

          {/* Close */}
          <button className="icon-btn close-btn" onClick={handleClose} aria-label="Close calendar">
            <FiX size={isMobile ? 18 : 20} />
          </button>
        </div>
      </div>

      {/* Event Type Filter Dropdown */}
      {showEventFilter && (
        <div className="filter-dropdown">
          <div className="filter-options">
            <button 
              className={`filter-option ${eventTypeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('all')}
            >
              All Events
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'exam' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('exam')}
            >
              Exams
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'meeting' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('meeting')}
            >
              Meetings
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'event' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('event')}
            >
              Events
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'holiday' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('holiday')}
            >
              Holidays
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'birthday' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('birthday')}
            >
              🎂 Birthdays
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'fee' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('fee')}
            >
              💰 Fees
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'fee_due' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('fee_due')}
            >
              ⏰ Fee Due
            </button>
            <button 
              className={`filter-option ${eventTypeFilter === 'fee_overdue' ? 'active' : ''}`}
              onClick={() => setEventTypeFilter('fee_overdue')}
            >
              ⚠️ Overdue Fees
            </button>
          </div>
        </div>
      )}

      {/* Calendar Body */}
      <div className={`calendar-body ${loading ? 'loading' : ''}`}>

        <div className="month-view">
          {/* Calendar grid with dynamic height allocation */}
          <div 
            className="calendar-grid"
            style={{
              '--cell-height': `${dynamicCellHeight}px`,
              '--cell-min-height': `${isCompact ? 70 : isMobile ? 80 : 90}px`,
              height: '100%' // Fill all available space in month-view
            }}
          >
            {getCalendarDays().map((day, index) => {
              try {
                const allDayEvents = getEventsForDate(day.fullDate);
                const { visibleEvents, hiddenCount, shouldShowMore } = getVisibleEventsAndMore(allDayEvents);
                
                // Check if this cell is currently loading
                const year = day.fullDate.getFullYear();
                const month = String(day.fullDate.getMonth() + 1).padStart(2, '0');
                const dayStr = String(day.fullDate.getDate()).padStart(2, '0');
                const cellDateStr = `${year}-${month}-${dayStr}`;
                const isCellLoading = loadingCellDate === cellDateStr;
              
              const isSelected = 
                day.isCurrentMonth &&
                selectedDate.getDate() === day.date &&
                selectedDate.getMonth() === day.fullDate.getMonth() &&
                selectedDate.getFullYear() === day.fullDate.getFullYear();
              
              // Check if this is the first row (index 0-6)
              const isFirstRow = index < 7;
              const weekdayName = isFirstRow ? DAY_NAMES[index] : null;

              return (
                <div
                  key={index}
                  className={`calendar-cell ${
                    day.isCurrentMonth ? 'current-month' : 'other-month'
                  } ${day.isToday ? 'today' : ''} ${
                    isSelected ? 'selected' : ''
                  } ${isFirstRow ? 'first-row' : ''}${
                    allDayEvents.length > 0 ? ' has-events' : ''
                  }`}
                  onClick={() => setSelectedDate(day.fullDate)}
                  onDoubleClick={(e) => handleCellDoubleClick(day.fullDate, e)}
                  title={`${day.isCurrentMonth ? '' : 'Other month - '}${day.fullDate.toLocaleDateString()}${allDayEvents.length > 0 ? ` - ${allDayEvents.length} event${allDayEvents.length !== 1 ? 's' : ''}` : ''} - Double-click to create event`}
                >
                  <div className="cell-header">
                    {isFirstRow && (
                      <div className="weekday-name">{weekdayName}</div>
                    )}
                    <span className={`day-number ${day.isToday ? 'today-number' : ''}`}>
                      {day.date}
                    </span>
                  </div>
                  
                  <div className="cell-events">
                    {allDayEvents.length === 0 && !loading && events.length === 0 && (
                      <div className="no-events-indicator">
                        {/* Empty state - could add a subtle indicator if needed */}
                      </div>
                    )}
                    
                    <div className="events-container">
                      {visibleEvents.map((event, eventIndex) => (
                        <div
                          key={event.id}
                          className={`event-item ${event.event_type || event.type} ${event.subtype || ''}`}
                          style={{ 
                            backgroundColor: event.backgroundColor || event.color,
                            zIndex: visibleEvents.length - eventIndex // Stack events properly
                          }}
                          title={`${event.title}${!event.allDay && event.start ? ` - ${formatTime(event.start)}` : ''}${event.description ? `\n${event.description}` : ''}`}
                          onClick={(e) => handleEventClick(event, e)}
                        >
                          <div className="event-content">
                            {(event.event_type === 'birthday' || event.type === 'birthday') && (
                              <span className="birthday-icon">🎂</span>
                            )}
                            {(event.event_type === 'fee' || event.type === 'fee') && event.subtype === 'due' && (
                              <span className="fee-icon fee-due">💰</span>
                            )}
                            {(event.event_type === 'fee' || event.type === 'fee') && event.subtype === 'overdue' && (
                              <span className="fee-icon fee-overdue">⚠️</span>
                            )}
                            {(event.event_type === 'fee' || event.type === 'fee') && event.subtype === 'individual' && (
                              <span className="fee-icon fee-individual">💳</span>
                            )}
                            <div className="event-title">{event.title}</div>
                            {!event.allDay && event.start && !isCompact && (
                              <div className="event-time">
                                {formatTime(event.start)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {shouldShowMore && (
                      <div 
                        className={`view-more-indicator ${isCellLoading ? 'loading' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!isCellLoading) {
                            handleViewMoreEvents(day.fullDate, allDayEvents, e);
                          }
                        }}
                        title={isCellLoading ? 'Loading...' : `View ${hiddenCount} more events`}
                      >
                        {isCellLoading ? (
                          <>
                            <div className="loading-spinner-small"></div>
                            <span className="more-text">Loading...</span>
                          </>
                        ) : (
                          <>
                            <span className="more-count">+{hiddenCount}</span>
                            <span className="more-text">more</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
              } catch (error) {
                console.error('Calendar Debug - Error rendering day:', {
                  date: day.fullDate?.toISOString?.() || day.fullDate,
                  error: error.message
                });
                
                // Return a minimal cell when there's an error
                return (
                  <div 
                    key={index}
                    className={`calendar-cell ${day.isCurrentMonth ? 'current-month' : 'other-month'}`}
                  >
                    <div className="date-number">{day.date}</div>
                    <div className="error-message">Error loading events</div>
                  </div>
                );
              }
            })}
          </div>
        </div>
      </div>

      {/* Simplified Day Events Modal */}
      {showDayEvents && (
        <div className="day-events-modal-overlay simple" onClick={closeDayEventsModal}>
          <div className="day-events-modal simple" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="header-info">
                <h3 className="date-title">
                  {selectedDayDate ? selectedDayDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  }) : ''}
                </h3>
                <span className="events-count">
                  {selectedDayEvents.length} event{selectedDayEvents.length !== 1 ? 's' : ''}
                </span>
              </div>
              <button className="close-btn" onClick={closeDayEventsModal} aria-label="Close">
                <FiX size={18} />
              </button>
            </div>
            
            <div className="modal-content">
              {selectedDayEvents.length === 0 ? (
                <div className="no-events">
                  <FiCalendar size={32} />
                  <p>No events for this day</p>
                </div>
              ) : (
                <div className="events-list">
                  {selectedDayEvents.map((event, index) => (
                    <div
                      key={event.id}
                      className={`event-item simple ${event.type}`}
                      onClick={(e) => handleEventClick(event, e)}
                    >
                      <div 
                        className="event-color-bar" 
                        style={{ backgroundColor: event.color }}
                      ></div>
                      
                      <div className="event-info">
                        <div className="event-title">
                          {event.type === 'birthday' && (
                            <span className="birthday-icon">🎂</span>
                          )}
                          {event.title}
                        </div>
                        <div className="event-meta">
                          <span className="event-time">
                            {event.isAllDay ? (
                              'All Day'
                            ) : (
                              <>
                                {formatTime(event.startTime)}
                                {event.endTime && ` - ${formatTime(event.endTime)}`}
                              </>
                            )}
                          </span>
                          <span className="event-type-badge">{event.type}</span>
                        </div>
                        {event.description && (
                          <div className="event-desc">{event.description}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Event Popover Modal */}
      {showEventPopover && selectedEvent && (
        <div 
          className="event-popover-overlay" 
          onClick={closeEventPopover}
        >
          <div 
            className={`event-popover arrow-${arrowPosition}`}
            style={{
              left: `${popoverPosition.x}px`,
              top: `${popoverPosition.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`popover-arrow ${arrowPosition}`}></div>
            
            <button 
              className="popover-close-btn" 
              onClick={closeEventPopover}
              aria-label="Close"
            >
              <FiX size={16} />
            </button>
            
            <div className="popover-content">
              <div className="event-header">
                <div 
                  className="event-color-indicator" 
                  style={{ backgroundColor: selectedEvent.color }}
                ></div>
                <h4 className="event-title">
                  {selectedEvent.type === 'birthday' && (
                    <span className="birthday-icon">🎂</span>
                  )}
                  {selectedEvent.type === 'fee' && selectedEvent.subtype === 'due' && (
                    <span className="fee-icon fee-due">💰</span>
                  )}
                  {selectedEvent.type === 'fee' && selectedEvent.subtype === 'overdue' && (
                    <span className="fee-icon fee-overdue">⚠️</span>
                  )}
                  {selectedEvent.type === 'fee' && selectedEvent.subtype === 'individual' && (
                    <span className="fee-icon fee-individual">💳</span>
                  )}
                  {selectedEvent.title}
                </h4>
              </div>
              
              <div className="event-details">
                <div className="detail-item">
                  <span className="detail-label">Time:</span>
                  <span className="detail-value">
                    {selectedEvent.allDay ? (
                      'All Day'
                    ) : (
                      <>
                        {formatTime(selectedEvent.start)}
                        {selectedEvent.end && ` - ${formatTime(selectedEvent.end)}`}
                      </>
                    )}
                  </span>
                </div>
                
                <div className="detail-item">
                  <span className="detail-label">Type:</span>
                  <span className={`detail-value event-type-badge ${selectedEvent.event_type || selectedEvent.type}`}>
                    {selectedEvent.event_type || selectedEvent.type}
                  </span>
                </div>

                <div className="detail-item">
                  <span className="detail-label">Status:</span>
                  <span className={`detail-value status-badge ${selectedEvent.status}`}>
                    {selectedEvent.status}
                  </span>
                </div>
                
                {selectedEvent.description && (
                  <div className="detail-item description">
                    <span className="detail-label">Description:</span>
                    <p className="detail-value">{selectedEvent.description}</p>
                  </div>
                )}
                
                {selectedEvent.location && (
                  <div className="detail-item">
                    <span className="detail-label">Location:</span>
                    <span className="detail-value">{selectedEvent.location}</span>
                  </div>
                )}

                {selectedEvent.creator && (
                  <div className="detail-item">
                    <span className="detail-label">Created by:</span>
                    <span className="detail-value">{selectedEvent.creator.name}</span>
                  </div>
                )}

                {selectedEvent.assignment_type && (
                  <div className="detail-item">
                    <span className="detail-label">Assigned to:</span>
                    <span className="detail-value">
                      {selectedEvent.assignment_summary ? (
                        `${selectedEvent.assignment_summary.description} (${selectedEvent.assignment_summary.count})`
                      ) : (
                        selectedEvent.assignment_details?.description || selectedEvent.assignment_type
                      )}
                    </span>
                  </div>
                )}
                
                {selectedEvent.instructor && (
                  <div className="detail-item">
                    <span className="detail-label">Instructor:</span>
                    <span className="detail-value">{selectedEvent.instructor}</span>
                  </div>
                )}
                
                {selectedEvent.type === 'birthday' && selectedEvent.details && (
                  <>
                    {selectedEvent.details.age && (
                      <div className="detail-item">
                        <span className="detail-label">Age:</span>
                        <span className="detail-value">{selectedEvent.details.age} years old</span>
                      </div>
                    )}
                    {selectedEvent.details.person_type && (
                      <div className="detail-item">
                        <span className="detail-label">Person Type:</span>
                        <span className="detail-value">{selectedEvent.details.person_type}</span>
                      </div>
                    )}
                    {selectedEvent.details.designation && (
                      <div className="detail-item">
                        <span className="detail-label">Designation:</span>
                        <span className="detail-value">{selectedEvent.details.designation}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Action Buttons */}
              {selectedEvent.is_creator && (
                <div className="popover-actions">
                  <button 
                    className="edit-event-btn"
                    onClick={() => handleEditEvent(selectedEvent)}
                    title="Edit this event"
                  >
                    Edit Event
                  </button>
                  <button 
                    className="delete-event-btn"
                    onClick={() => handleDeleteEvent(selectedEvent)}
                    title="Delete this event"
                  >
                    Delete Event
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View More Events Popover */}
      {showViewMorePopover && viewMoreEvents.length > 0 && (
        <div 
          className="event-popover-overlay" 
          onClick={closeViewMorePopover}
        >
          <div 
            className={`view-more-popover arrow-${viewMoreArrowPosition}`}
            style={{
              left: `${viewMorePosition.x}px`,
              top: `${viewMorePosition.y}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`popover-arrow ${viewMoreArrowPosition}`}></div>
            
            <button 
              className="popover-close-btn" 
              onClick={closeViewMorePopover}
              aria-label="Close"
            >
              <FiX size={16} />
            </button>
            
            <div className="popover-content">
              <div className="popover-header">
                <h4 className="popover-title">
                  {viewMoreDate ? viewMoreDate.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric'
                  }) : 'Events'}
                </h4>
                <span className="events-count">
                  {viewMoreEvents.length} event{viewMoreEvents.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="events-list-container">
                {viewMoreEvents.length === 0 ? (
                  <div className="no-events">
                    <div className="empty-icon">📅</div>
                    <p>No events for this day</p>
                  </div>
                ) : (
                  viewMoreEvents.map((event, index) => (
                    <div
                      key={event.id}
                      className={`popover-event-item ${event.type}`}
                      onClick={(e) => {
                        closeViewMorePopover();
                        handleEventClick(event, e);
                      }}
                    >
                      <div 
                        className="event-color-indicator" 
                        style={{ backgroundColor: event.backgroundColor || event.color }}
                      ></div>
                      
                      <div className="event-info">
                        <div className="event-title">
                          {(event.event_type === 'birthday' || event.type === 'birthday') && (
                            <span className="birthday-icon">🎂</span>
                          )}
                          {event.title}
                        </div>
                        <div className="event-meta">
                          <span className="event-time">
                            {event.allDay ? (
                              'All Day'
                            ) : (
                              <>
                                {formatTime(event.start)}
                                {event.end && ` - ${formatTime(event.end)}`}
                              </>
                            )}
                          </span>
                          <span className={`event-type-badge ${event.event_type || event.type}`}>
                            {event.event_type || event.type}
                          </span>
                        </div>
                        {event.description && (
                          <div className="event-desc">{event.description}</div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      <CreateEventModal
        isOpen={showCreateEventModal}
        onClose={handleCreateEventClose}
        selectedDate={createEventDate}
        clickPosition={createEventPosition}
        arrowDirection={createEventArrowDirection}
        editingEvent={editingEvent}
        isEditMode={isEditMode}
      />
    </div>
  );

  // Always return as fullscreen modal using createPortal
  return createPortal(
    <CalendarContent />,
    document.body
  );
};

export default ProtonCalendar;
